import { isAfter, parseISO } from 'date-fns';
import { Integration, IntegrationState } from 'types';

/**
 * Derives the effective IntegrationState from an integration object.
 * `state` is the source of truth (GLX-1602); `archivedAt` remains as a defensive
 * fallback for the rare integration that predates the `state` field.
 */
export const getEffectiveState = (
  integration: Integration | undefined,
): IntegrationState | undefined => {
  if (!integration) return undefined;
  if (integration.state) return integration.state;
  if (integration.archivedAt) return IntegrationState.ARCHIVED;
  return IntegrationState.ACTIVE;
};

export const isIntegrationArchived = (integration: Integration): boolean => {
  if (integration.state) {
    return integration.state === IntegrationState.ARCHIVED;
  }
  return !!integration.archivedAt;
};

/**
 * Groups a team's integrations by targetService, keeping only rows that are not
 * archived (INTG-13144). A service whose rows are all archived is absent from the
 * result, so a caller that fans out one health request per key never requests it.
 */
export const groupNonArchivedTargetServiceIds = (
  integrations: Integration[] | undefined,
): Record<string, number[]> => {
  const targetServiceToIds: Record<string, number[]> = {};
  integrations?.forEach((integration) => {
    if (isIntegrationArchived(integration)) return;
    const { targetService, targetServiceId } = integration;
    if (targetServiceToIds[targetService]) {
      targetServiceToIds[targetService].push(targetServiceId);
    } else {
      targetServiceToIds[targetService] = [targetServiceId];
    }
  });
  return targetServiceToIds;
};

// Assumption is there is only at most 1 active integration per target service id group (checked)
export const findMostRecentlyArchivedIntegrationAndOnlyActiveIntegrationInTargetServiceIdGroup = (
  integrationsInTargetServiceIdGroup?: Integration[],
) => {
  let activeIntegration: Integration | undefined;
  let mostRecentlyArchivedIntegration: Integration | undefined;

  if (!integrationsInTargetServiceIdGroup)
    return {
      activeIntegration,
      mostRecentlyArchivedIntegration,
    };

  integrationsInTargetServiceIdGroup.forEach((integration) => {
    if (!isIntegrationArchived(integration)) {
      // found active (non-archived) integration
      activeIntegration = integration;
    } else {
      // archived
      if (!mostRecentlyArchivedIntegration) {
        // Defined if not exist yet
        mostRecentlyArchivedIntegration = integration;
      } else {
        // already exist, check which one is most recently archived
        // Prefer integrations with valid archivedAt timestamps
        const currentHasTimestamp = !!integration.archivedAt;
        const existingHasTimestamp = !!mostRecentlyArchivedIntegration.archivedAt;

        if (currentHasTimestamp && existingHasTimestamp) {
          // Both have timestamps - compare them
          // Non-null assertion safe: checked via currentHasTimestamp && existingHasTimestamp
          mostRecentlyArchivedIntegration = isAfter(
            parseISO(integration.archivedAt!),
            parseISO(mostRecentlyArchivedIntegration.archivedAt!),
          )
            ? integration
            : mostRecentlyArchivedIntegration;
        } else if (currentHasTimestamp && !existingHasTimestamp) {
          // Current has timestamp, existing doesn't - prefer current
          mostRecentlyArchivedIntegration = integration;
        }
        // If existing has timestamp but current doesn't, keep existing (do nothing)
        // If neither has timestamp, keep existing (do nothing)
      }
    }
  });

  return {
    activeIntegration,
    mostRecentlyArchivedIntegration,
  };
};
